import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocSignatures,
  DocContactQR,
  DocInlineDate,
  DocInlineAresName,
  DocInlineAresLookup,
  DocActionButton,
  DocDphSelect,
  SavedDocsButton,
  NewDocButton,
  DraftBanner,
  useDocChain,
  useDocTransferIn,
  DocChainBar,
  DocTransferButton,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
} from '../../components/document';
import { MapPickerButton } from '../../components/map';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  type AresPickResult,
  type CompanyPickResult,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { useSaveDocument } from '../../api/hooks';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { fmtCzNum, previousZp } from '../../lib/docChain';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useActiveStavba } from '../stavba-context/StavbaContext';

/**
 * Zjišťovací protokol — "živý dokument".
 * Sekce: Za období / Objednatel / Zhotovitel / Stavba+SOD+DOD /
 * Provedené práce (s inline editovatelnými částkami a DPH) / Prohlášení / Podpisy.
 */

interface DodItem {
  id: string;
  label: string;
  castka: string;
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function parseCz(s: string): number {
  if (!s) return 0;
  const cleaned = s
    .replace(/\s/g, '')
    .replace(/Kč/gi, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');
  const n = parseFloat(cleaned);
  return isFinite(n) ? n : 0;
}

function fmtCz(n: number): string {
  return (
    n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Kč'
  );
}

function deriveInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w && /[\p{L}]/u.test(w[0]) && !/^(s\.?r\.?o\.?|a\.?s\.?|spol|ltd|inc)$/i.test(w));
  if (words.length === 0) return '—';
  return words
    .slice(0, 3)
    .map((w) => w[0].toLocaleUpperCase('cs'))
    .join('');
}

const PROHLASENI_TEXT =
  'Podepsáním prohlašuji, že objem prací a dodávek, uvedený v tomto protokolu a specifikovaný ' +
  'v přiložených soupisech provedených prací, odpovídá platné projektové dokumentaci a jejich ' +
  'uvedený objem odpovídá skutečně provedeným pracím a dodávkám. Zhotovitel předal a objednatel ' +
  'převzal zjištěné práce.';

export function ZjistovaciPage() {
  const saveDoc = useSaveDocument();
  const toast = useToast();
  const { activeStavba } = useActiveStavba();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const [cislo, setCislo] = useState('');
  const [zaObdobi, setZaObdobi] = useState('');
  const [datum, setDatum] = useState('');
  const [zakazka, setZakazka] = useState('');

  const [obj, setObj] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    tel: '',
    email: '',
  });
  const [zhot, setZhot] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    tel: '',
    email: '',
  });
  const [stavba, setStavba] = useState({
    nazev: '',
    iniciály: '',
    adresa: '',
    cPovoleni: '',
    kontrolniCislo: '',
    sodCislo: '',
    sodDatum: '',
    sodCastka: '',
  });
  const [dodList, setDodList] = useState<DodItem[]>([]);
  const [prace, setPrace] = useState({ prev: '0', curr: '' });
  const [dphPct, setDphPct] = useState('21');

  // Skryté řádky — uživatel může jednotlivé řádky odebrat ✕,
  // skupinové „obnovit" je pak v hlavičce sekce. Plus vlastní řádky v sekcích.
  const chain = useDocChain();
  const rows = useDocRows();
  const { isHidden, hide, restoreSection, hiddenCount } = rows;

  const [podpisy, setPodpisy] = useState({
    zhotovitel: { jmeno: '', funkce: '', image: '' },
    objednatel: { jmeno: '', funkce: '', image: '' },
    extra: [] as Array<{ id: string; strana: string; jmeno: string; funkce: string; image: string }>,
  });

  const calc = useMemo(() => {
    const sod = parseCz(stavba.sodCastka);
    const dodSum = dodList.reduce((a, d) => a + parseCz(d.castka), 0);
    const celkem = sod + dodSum;

    const prev = parseCz(prace.prev);
    const curr = parseCz(prace.curr);
    const total = prev + curr;

    const rate = (parseFloat(dphPct) || 0) / 100;
    const dphPrev = prev * rate;
    const dphCurr = curr * rate;
    const dphTot = total * rate;

    return {
      sod, dodSum, celkem,
      prev, curr, total,
      rate, dphPrev, dphCurr, dphTot,
      r5Prev: prev + dphPrev,
      r5Curr: curr + dphCurr,
      r5Tot: total + dphTot,
    };
  }, [stavba.sodCastka, dodList, prace.prev, prace.curr, dphPct]);

  const docTitle = cislo ? `Zjišťovací protokol č. ${cislo}` : 'Zjišťovací protokol';
  const metaLine = [zaObdobi, datum].filter(Boolean).join(' · ') || undefined;

  /** Předvyplní formulář ze snímku dat (dokument z archivu i lokální koncept). */
  function applySnapshot(d: Record<string, unknown>) {
    if (d.cislo != null) setCislo(String(d.cislo));
    if (d.zaObdobi != null) setZaObdobi(String(d.zaObdobi));
    if (d.datum != null) setDatum(String(d.datum));
    if (d.zakazka != null) setZakazka(String(d.zakazka));
    if (d.obj) setObj((x) => ({ ...x, ...(d.obj as Partial<typeof obj>) }));
    if (d.zhot) setZhot((x) => ({ ...x, ...(d.zhot as Partial<typeof zhot>) }));
    if (d.stavba) setStavba((s) => ({ ...s, ...(d.stavba as Partial<typeof stavba>) }));
    if (Array.isArray(d.dodList)) setDodList(d.dodList as DodItem[]);
    if (d.prace) setPrace((p) => ({ ...p, ...(d.prace as Partial<typeof prace>) }));
    if (d.dphPct != null) setDphPct(String(d.dphPct));
    if (d.podpisy) setPodpisy((p) => ({ ...p, ...(d.podpisy as Partial<typeof podpisy>) }));
    chain.apply(d);
    rows.apply(d.radky);
  }

  const docData = {
    cislo, zaObdobi, datum, zakazka,
    obj, zhot, stavba, dodList,
    prace, dphPct, podpisy,
    radky: rows.snapshot(),
    zdroj: chain.zdroj,
  };
  const { editId } = useDocEditor(applySnapshot);
  useDocTransferIn('zjistovaci', applySnapshot, editId);

  // Autosave rozpracovaného konceptu do prohlížeče (jen u nového dokumentu) —
  // bez něj se při zavření/reloadu záložky ztratil celý rozepsaný formulář.
  const draft = useDocDraft<Record<string, unknown>>('zjistovaci', editId == null);
  function restoreDraft() {
    if (!draft.pending) return;
    applySnapshot(draft.pending.data);
    draft.accept();
  }
  useEffect(() => {
    draft.persist(docData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cislo, zaObdobi, datum, zakazka, obj, zhot, stavba, dodList, prace, dphPct, podpisy, rows, chain.zdroj]);

  /**
   * Uložení do archivu. Chybu je nutné ukázat — dřív se `mutateAsync` odmítlo
   * bez ošetření a uživatel si myslel, že je dokument uložený.
   */
  async function handleSave(): Promise<number | null> {
    try {
      const res = await saveDoc.mutateAsync({
        id: editId ?? chain.savedId ?? undefined,
        doc_type: 'zjistovaci',
        title: docTitle,
        stavba: activeStavba?.nazev ?? '',
        data_json: docData,
      });
      draft.clear();
      toast.success('Zjišťovací protokol uložen do archivu.');
      chain.markSaved(res.id);
      return res.id;
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
      return null;
    }
  }

  /**
   * „Dosud provedeno" = součet „toto období" ze všech dřívějších ZP téže zakázky
   * (stejný řetězec dokladů, u ručně založených ZP stejné číslo zakázky).
   */
  async function dopocitatPredchozi() {
    try {
      const { count, sum } = await previousZp(chain.zdroj?.koren ?? null, zakazka, editId ?? chain.savedId);
      if (count === 0) {
        toast.error(
          chain.zdroj || zakazka.trim()
            ? 'Žádný předchozí ZP k této zakázce v archivu není.'
            : 'Nejde dohledat — vyplňte číslo zakázky, nebo ZP založte převodem ze soupisu.',
        );
        return;
      }
      setPrace((p) => ({ ...p, prev: fmtCzNum(sum) }));
      toast.success(`Dosud provedeno: součet předchozích ZP (počet: ${count}).`);
    } catch {
      toast.error('Předchozí ZP se nepodařilo načíst.');
    }
  }

  const addDod = () =>
    setDodList((d) => [
      ...d,
      { id: newId('dod'), label: `Dodatek ${d.length + 1}`, castka: '' },
    ]);
  const updateDod = (id: string, p: Partial<DodItem>) =>
    setDodList((d) => d.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const removeDod = (id: string) => setDodList((d) => d.filter((x) => x.id !== id));

  const addExtra = () =>
    setPodpisy((p) => ({
      ...p,
      extra: [
        ...p.extra,
        { id: newId('ex'), strana: 'Účastník', jmeno: '', funkce: '', image: '' },
      ],
    }));
  const updateExtra = (id: string, patch: Partial<{ strana: string; jmeno: string; funkce: string; image: string }>) =>
    setPodpisy((p) => ({
      ...p,
      extra: p.extra.map((x) => (x.id === id ? { ...x, ...patch } : x)),
    }));
  const removeExtra = (id: string) =>
    setPodpisy((p) => ({ ...p, extra: p.extra.filter((x) => x.id !== id) }));

  const setObjPatch = (patch: Partial<typeof obj>) => setObj((o) => ({ ...o, ...patch }));
  const setZhotPatch = (patch: Partial<typeof zhot>) => setZhot((z) => ({ ...z, ...patch }));
  const setStavbaPatch = (patch: Partial<typeof stavba>) => setStavba((s) => ({ ...s, ...patch }));

  return (
    <>
      <DocLayout
        breadcrumb={['Dokumenty', 'Zjišťovací protokol']}
        title={docTitle}
        statusText="Rozpracovaný koncept"
        status="draft"
        actions={
          <>
            <NewDocButton draftKey="zjistovaci" path="/app/zjistovaci" />
            <SavedDocsButton
              docType="zjistovaci"
              title="Uložené zjišťovací protokoly"
              currentId={editId}
            />
            <Button variant="secondary" leading="⎙" onClick={() => printToPdf(`Zjistovaci_protokol_${cislo || 'koncept'}.pdf`)}>PDF</Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <DocTransferButton
              docType="zjistovaci"
              getData={() => docData}
              save={handleSave}
              title={docTitle}
              zdroj={chain.zdroj}
            />
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        <DocChainBar zdroj={chain.zdroj}>
          <Button size="sm" variant="ghost" onClick={() => void dopocitatPredchozi()}>
            ↻ Dosud provedeno z předchozích ZP
          </Button>
        </DocChainBar>
        {draft.pending && (
          <DraftBanner
            savedAt={draft.pending.savedAt}
            onRestore={restoreDraft}
            onDiscard={draft.clear}
          />
        )}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: stavba.iniciály || deriveInitials(stavba.nazev || zhot.nazev),
              name: zhot.nazev || '—',
              address: `${zhot.sidlo || '—'}${zhot.ico ? ' · IČ ' + zhot.ico : ''}`,
            }}
            tag="Zjišťovací protokol"
            title={
              <>
                {docTitle}{' '}
                <span style={{ fontWeight: 400, fontSize: 14, color: 'var(--sd-doc-dim)' }}>
                  · provedených prací
                </span>
              </>
            }
            meta={metaLine}
            footer={{ left: zhot.nazev || '—', center: 'str. 1 / 1', right: zakazka || '—' }}
          >
            <DocBlock
              idx="01"
              title="Údaje protokolu"
              hiddenCount={hiddenCount('s01')}
              onRestoreHidden={() => restoreSection('s01')}
              actions={<DocAddRowButton rows={rows} section="s01" />}
            >
              {!isHidden('s01.cislo') && (
                <DocRow
                  k="Číslo protokolu"
                  mono
                  value={cislo}
                  onChange={setCislo}
                  placeholder="např. 1"
                  onRemove={() => hide('s01.cislo')}
                />
              )}
              {!isHidden('s01.obdobi') && (
                <DocRow
                  k="Za období"
                  value={zaObdobi}
                  onChange={setZaObdobi}
                  placeholder="např. Duben 2026"
                  onRemove={() => hide('s01.obdobi')}
                />
              )}
              {!isHidden('s01.datum') && (
                <DocRow
                  k="Datum vyhotovení"
                  mono
                  value={datum}
                  onChange={setDatum}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={datum} onChange={setDatum} />}
                  onRemove={() => hide('s01.datum')}
                />
              )}
              {!isHidden('s01.zakazka') && (
                <DocRow
                  k="Číslo zakázky"
                  mono
                  value={zakazka}
                  onChange={setZakazka}
                  placeholder="např. Z-2026/0412"
                  onRemove={() => hide('s01.zakazka')}
                />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Objednatel"
              hiddenCount={hiddenCount('s02')}
              onRestoreHidden={() => restoreSection('s02')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s02" />
                  <CompanyPickerButton
                    preferRole="objednatel"
                    title="Načíst objednatele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setObj({
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit objednatele do adresáře"
                    build={() => ({
                      role: 'objednatel',
                      nazev: obj.nazev,
                      ico: obj.ico,
                      dic: obj.dic,
                      adresa: obj.sidlo,
                      kontakt_tel: obj.tel,
                      kontakt_email: obj.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s02.nazev') && (
                <DocRow
                  k="Název"
                  v={
                    <DocInlineAresName
                      value={obj.nazev}
                      onChange={(v) => setObjPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setObjPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s02.nazev')}
                />
              )}
              {!isHidden('s02.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={obj.sidlo}
                  onChange={(v) => setObjPatch({ sidlo: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={obj.sidlo}
                      onApply={(v) => setObjPatch({ sidlo: v })}
                      title="Sídlo objednatele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s02.sidlo')}
                />
              )}
              {!isHidden('s02.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={obj.ico}
                  onChange={(v) => setObjPatch({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={obj.ico}
                      onResult={(d) => setObjPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })}
                    />
                  }
                  onRemove={() => hide('s02.ico')}
                />
              )}
              {!isHidden('s02.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={obj.dic}
                  onChange={(v) => setObjPatch({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s02.dic')}
                />
              )}
              {!isHidden('s02.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={obj.tel}
                  onChange={(v) => setObjPatch({ tel: v })}
                  placeholder="+420 …"
                  inputType="tel"
                  onRemove={() => hide('s02.tel')}
                />
              )}
              {!isHidden('s02.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={obj.email}
                  onChange={(v) => setObjPatch({ email: v })}
                  placeholder="email@…"
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={obj.nazev}
                      role="Objednatel"
                      phone={obj.tel}
                      email={obj.email}
                      title="QR vizitka — objednatel"
                    />
                  }
                  onRemove={() => hide('s02.email')}
                />
              )}
              <DocCustomRows rows={rows} section="s02" />
            </DocBlock>

            <DocBlock
              idx="03"
              title="Zhotovitel"
              hiddenCount={hiddenCount('s03')}
              onRestoreHidden={() => restoreSection('s03')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s03" />
                  <CompanyPickerButton
                    preferRole="zhotovitel"
                    title="Načíst zhotovitele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setZhot({
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit zhotovitele do adresáře"
                    build={() => ({
                      role: 'zhotovitel',
                      nazev: zhot.nazev,
                      ico: zhot.ico,
                      dic: zhot.dic,
                      adresa: zhot.sidlo,
                      kontakt_tel: zhot.tel,
                      kontakt_email: zhot.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s03.nazev') && (
                <DocRow
                  k="Název"
                  v={
                    <DocInlineAresName
                      value={zhot.nazev}
                      onChange={(v) => setZhotPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setZhotPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.nazev')}
                />
              )}
              {!isHidden('s03.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={zhot.sidlo}
                  onChange={(v) => setZhotPatch({ sidlo: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={zhot.sidlo}
                      onApply={(v) => setZhotPatch({ sidlo: v })}
                      title="Sídlo zhotovitele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s03.sidlo')}
                />
              )}
              {!isHidden('s03.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={zhot.ico}
                  onChange={(v) => setZhotPatch({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={zhot.ico}
                      onResult={(d) => setZhotPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })}
                    />
                  }
                  onRemove={() => hide('s03.ico')}
                />
              )}
              {!isHidden('s03.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={zhot.dic}
                  onChange={(v) => setZhotPatch({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s03.dic')}
                />
              )}
              {!isHidden('s03.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={zhot.tel}
                  onChange={(v) => setZhotPatch({ tel: v })}
                  placeholder="+420 …"
                  inputType="tel"
                  onRemove={() => hide('s03.tel')}
                />
              )}
              {!isHidden('s03.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={zhot.email}
                  onChange={(v) => setZhotPatch({ email: v })}
                  placeholder="email@…"
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={zhot.nazev}
                      role="Zhotovitel"
                      phone={zhot.tel}
                      email={zhot.email}
                      title="QR vizitka — zhotovitel"
                    />
                  }
                  onRemove={() => hide('s03.email')}
                />
              )}
              <DocCustomRows rows={rows} section="s03" />
            </DocBlock>

            <DocBlock
              idx="04"
              title="Stavba a smlouva o dílo"
              hiddenCount={hiddenCount('s04')}
              onRestoreHidden={() => restoreSection('s04')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s04" />
                  <StavbaPickerButton
                    title="Načíst stavbu ze seznamu"
                    onPick={(p: StavbaPickResult) =>
                      setStavba((s) => ({
                        ...s,
                        nazev: p.nazev,
                        iniciály: deriveInitials(p.nazev),
                        adresa: p.adresa,
                        cPovoleni: p.stavebni_povoleni,
                        sodDatum: p.datum_zahajeni,
                      }))
                    }
                  />
                  <SaveStavbaButton
                    title="Uložit stavbu"
                    build={() => ({
                      nazev: stavba.nazev,
                      adresa: stavba.adresa,
                      stavebni_povoleni: stavba.cPovoleni,
                      zakazka_cislo: zakazka,
                      datum_zahajeni: stavba.sodDatum,
                    })}
                  />
                  <DocActionButton onClick={addDod} title="Přidat dodatek SOD">
                    + DOD
                  </DocActionButton>
                </>
              }
            >
              {!isHidden('s04.nazev') && (
                <DocRow
                  k="Název stavby"
                  value={stavba.nazev}
                  placeholder="např. Novostavba RD Březník"
                  onChange={(v) => {
                    const auto = deriveInitials(stavba.nazev);
                    setStavba((s) => ({
                      ...s,
                      nazev: v,
                      iniciály: !s.iniciály || s.iniciály === auto ? deriveInitials(v) : s.iniciály,
                    }));
                  }}
                  onRemove={() => hide('s04.nazev')}
                />
              )}
              {!isHidden('s04.iniciaty') && (
                <DocRow
                  k="Iniciály"
                  mono
                  value={stavba.iniciály}
                  onChange={(v) => setStavbaPatch({ iniciály: v })}
                  placeholder={deriveInitials(stavba.nazev)}
                  onRemove={() => hide('s04.iniciaty')}
                />
              )}
              {!isHidden('s04.adresa') && (
                <DocRow
                  k="Adresa staveniště"
                  value={stavba.adresa}
                  onChange={(v) => setStavbaPatch({ adresa: v })}
                  placeholder="např. Březník 142, 675 74"
                  actions={
                    <MapPickerButton
                      value={stavba.adresa}
                      onApply={(v) => setStavbaPatch({ adresa: v })}
                      title="Adresa staveniště"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s04.adresa')}
                />
              )}
              {!isHidden('s04.povoleni') && (
                <DocRow
                  k="Číslo stavebního povolení"
                  value={stavba.cPovoleni}
                  onChange={(v) => setStavbaPatch({ cPovoleni: v })}
                  placeholder="např. MÚ-2026/0341"
                  onRemove={() => hide('s04.povoleni')}
                />
              )}
              {!isHidden('s04.kontrolni') && (
                <DocRow
                  k="Kontrolní číslo"
                  mono
                  value={stavba.kontrolniCislo}
                  onChange={(v) => setStavbaPatch({ kontrolniCislo: v })}
                  onRemove={() => hide('s04.kontrolni')}
                />
              )}
              {!isHidden('s04.sodCislo') && (
                <DocRow
                  k="Smlouva o dílo č."
                  mono
                  value={stavba.sodCislo}
                  onChange={(v) => setStavbaPatch({ sodCislo: v })}
                  placeholder="např. SOD-2026/04"
                  onRemove={() => hide('s04.sodCislo')}
                />
              )}
              {!isHidden('s04.sodDatum') && (
                <DocRow
                  k="Smlouva o dílo ze dne"
                  mono
                  value={stavba.sodDatum}
                  onChange={(v) => setStavbaPatch({ sodDatum: v })}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={stavba.sodDatum}
                      onChange={(v) => setStavbaPatch({ sodDatum: v })}
                    />
                  }
                  onRemove={() => hide('s04.sodDatum')}
                />
              )}
              {!isHidden('s04.sodCastka') && (
                <DocRow
                  k="Smluvní částka dle SOD bez DPH (Kč)"
                  mono
                  value={stavba.sodCastka}
                  onChange={(v) => setStavbaPatch({ sodCastka: v })}
                  placeholder="např. 562 300"
                  onRemove={() => hide('s04.sodCastka')}
                />
              )}
              {dodList.map((d, i) => (
                <DocRow
                  key={d.id}
                  k={
                    <input
                      value={d.label}
                      onChange={(e) => updateDod(d.id, { label: e.target.value })}
                      style={dodLabelInput}
                      placeholder={`Dodatek ${i + 1} (popis)`}
                      title="Popis dodatku — klikni a uprav"
                    />
                  }
                  mono
                  value={d.castka}
                  onChange={(v) => updateDod(d.id, { castka: v })}
                  placeholder="0"
                  actions={
                    <DocActionButton
                      onClick={() => removeDod(d.id)}
                      title="Odebrat tento dodatek"
                    >
                      ✕
                    </DocActionButton>
                  }
                />
              ))}
              <DocRow
                k="Smluvní částka bez DPH (celkem)"
                mono
                v={
                  <strong style={{ fontFamily: 'var(--sd-font-mono)' }}>
                    {calc.celkem ? fmtCz(calc.celkem) : '—'}
                  </strong>
                }
              />
              <DocCustomRows rows={rows} section="s04" />
            </DocBlock>

            <DocBlock
              idx="05"
              title="Provedené práce"
              actions={
                <DocDphSelect value={dphPct} onChange={setDphPct} />
              }
            >
              <div style={{ margin: '4px 0 10px', overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Položka</th>
                      <th style={thStyle}>
                        Od zahájení do konce<br />předchozího období
                      </th>
                      <th style={thStyle}>
                        Probíhající<br />období
                      </th>
                      <th style={thStyle}>
                        Od zahájení do konce<br />probíhajícího období
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={tdStyle}>Provedené práce</td>
                      <td style={tdRight}>
                        <input
                          value={prace.prev}
                          onChange={(e) => setPrace({ ...prace, prev: e.target.value })}
                          style={cellInput}
                          placeholder="0"
                        />
                      </td>
                      <td style={tdRight}>
                        <input
                          value={prace.curr}
                          onChange={(e) => setPrace({ ...prace, curr: e.target.value })}
                          style={cellInput}
                          placeholder="0"
                        />
                      </td>
                      <td style={tdRight}>{fmtCz(calc.total)}</td>
                    </tr>
                    <tr style={{ background: 'rgba(18,50,90,0.04)' }}>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>Provedené práce celkem</td>
                      <td style={{ ...tdRight, fontWeight: 700 }}>{fmtCz(calc.prev)}</td>
                      <td style={{ ...tdRight, fontWeight: 700 }}>{fmtCz(calc.curr)}</td>
                      <td style={{ ...tdRight, fontWeight: 700 }}>{fmtCz(calc.total)}</td>
                    </tr>
                    <tr style={{ background: 'rgba(18,50,90,0.04)' }}>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>K úhradě (bez DPH)</td>
                      <td style={{ ...tdRight, fontWeight: 700 }}>{fmtCz(calc.prev)}</td>
                      <td style={{ ...tdRight, fontWeight: 700 }}>{fmtCz(calc.curr)}</td>
                      <td style={{ ...tdRight, fontWeight: 700 }}>{fmtCz(calc.total)}</td>
                    </tr>
                    <tr>
                      <td style={tdStyle}>Faktura, DPH {dphPct} %</td>
                      <td style={tdRight}>{fmtCz(calc.dphPrev)}</td>
                      <td style={tdRight}>{fmtCz(calc.dphCurr)}</td>
                      <td style={tdRight}>{fmtCz(calc.dphTot)}</td>
                    </tr>
                    <tr style={{ background: 'var(--sd-yellow-soft)' }}>
                      <td style={{ ...tdStyle, fontWeight: 800 }}>K ÚHRADĚ</td>
                      <td style={{ ...tdRight, fontWeight: 800 }}>{fmtCz(calc.r5Prev)}</td>
                      <td style={{ ...tdRight, fontWeight: 800 }}>{fmtCz(calc.r5Curr)}</td>
                      <td style={{ ...tdRight, fontWeight: 800 }}>{fmtCz(calc.r5Tot)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </DocBlock>

            <DocBlock idx="06" title="Prohlášení">
              <DocRow
                k="Text"
                v={
                  <span
                    style={{
                      fontSize: 11.5,
                      lineHeight: 1.55,
                      color: 'var(--sd-doc-ink)',
                    }}
                  >
                    {PROHLASENI_TEXT}
                  </span>
                }
              />
              <DocRow
                k="Datum"
                mono
                value={datum}
                onChange={setDatum}
                placeholder="dd. mm. rrrr"
                actions={<DocInlineDate value={datum} onChange={setDatum} />}
              />
            </DocBlock>

            <DocBlock
              idx="07"
              title="Podpisy"
              actions={
                <DocActionButton onClick={addExtra} title="Přidat dalšího účastníka">
                  + Účastník
                </DocActionButton>
              }
            >
              <DocRow
                k="Zhotovitel — jméno"
                value={podpisy.zhotovitel.jmeno}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, zhotovitel: { ...p.zhotovitel, jmeno: v } }))
                }
              />
              <DocRow
                k="Zhotovitel — funkce"
                value={podpisy.zhotovitel.funkce}
                placeholder="např. stavbyvedoucí"
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, zhotovitel: { ...p.zhotovitel, funkce: v } }))
                }
              />
              <DocRow
                k="Objednatel — jméno"
                value={podpisy.objednatel.jmeno}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, objednatel: { ...p.objednatel, jmeno: v } }))
                }
              />
              <DocRow
                k="Objednatel — funkce"
                value={podpisy.objednatel.funkce}
                placeholder="např. investor"
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, objednatel: { ...p.objednatel, funkce: v } }))
                }
              />
              {podpisy.extra.map((e, i) => (
                <Fragment key={e.id}>
                  <DocRow
                    k={`Účastník ${i + 1} — strana`}
                    value={e.strana}
                    onChange={(v) => updateExtra(e.id, { strana: v })}
                    actions={
                      <DocActionButton onClick={() => removeExtra(e.id)} title="Odebrat účastníka">
                        ✕
                      </DocActionButton>
                    }
                  />
                  <DocRow
                    k="Jméno"
                    value={e.jmeno}
                    onChange={(v) => updateExtra(e.id, { jmeno: v })}
                  />
                  <DocRow
                    k="Funkce"
                    value={e.funkce}
                    onChange={(v) => updateExtra(e.id, { funkce: v })}
                  />
                </Fragment>
              ))}
            </DocBlock>

            <DocSignatures
              items={[
                {
                  label: 'Zhotovitel',
                  name: podpisy.zhotovitel.jmeno || '—',
                  role: podpisy.zhotovitel.funkce || '',
                  image: podpisy.zhotovitel.image,
                  onSign: (v) =>
                    setPodpisy((p) => ({ ...p, zhotovitel: { ...p.zhotovitel, image: v } })),
                },
                {
                  label: 'Objednatel',
                  name: podpisy.objednatel.jmeno || '—',
                  role: podpisy.objednatel.funkce || '',
                  image: podpisy.objednatel.image,
                  onSign: (v) =>
                    setPodpisy((p) => ({ ...p, objednatel: { ...p.objednatel, image: v } })),
                },
                ...podpisy.extra.map((e) => ({
                  label: e.strana || 'Účastník',
                  name: e.jmeno || '—',
                  role: e.funkce,
                  image: e.image,
                  onSign: (v: string) => updateExtra(e.id, { image: v }),
                })),
              ]}
            />
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="zjistovaci"
        docTitle={docTitle}
        defaultTo={obj.email}
        defaultCc={zhot.email}
        defaultSubject={`${docTitle} — ${stavba.nazev || 'stavba'}`}
        meta={`Stavba: ${stavba.nazev || '—'}${
          activeStavba?.nazev ? ' · ' + activeStavba.nazev : ''
        }`}
      />
    </>
  );
}

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
  color: 'var(--sd-doc-ink)',
};
const thStyle: CSSProperties = {
  padding: '6px 8px',
  border: '1px solid #d0d5de',
  background: 'rgba(18,50,90,0.06)',
  fontWeight: 700,
  fontSize: 10.5,
  textAlign: 'center',
  color: 'var(--sd-doc-ink)',
  lineHeight: 1.3,
};
const tdStyle: CSSProperties = {
  padding: '6px 8px',
  border: '1px solid #d0d5de',
  fontSize: 11.5,
};
const tdRight: CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontFamily: 'var(--sd-font-mono)',
};
const cellInput: CSSProperties = {
  width: '100%',
  background: 'transparent',
  border: 0,
  borderBottom: '1px dashed #d0d5de',
  outline: 0,
  padding: '2px 0',
  textAlign: 'right',
  fontFamily: 'var(--sd-font-mono)',
  fontSize: 11.5,
  color: 'var(--sd-doc-ink)',
};
const dodLabelInput: CSSProperties = {
  width: '100%',
  background: 'transparent',
  border: 0,
  outline: 0,
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'var(--sd-doc-dim)',
  fontWeight: 600,
};
